/**
 * Carrying rules and throw tuning — pure numbers and functions the engine applies.
 * Whether the player *is* carrying lives in the store; the physics of holding lives in
 * `src/engine/CarryController.tsx`.
 */

import type { LevelSpec, Vec3 } from './profile/types';
import { DEFAULT_CEILING_HEIGHT } from './profile/validate';

/**
 * The height a carried object — and anything riding on it — must stay under, or `null`
 * where the level has no roof to stay under.
 *
 * Indoors the clamp is what stops a stack being released inside the ceiling slab and
 * escaping the house. A grounds level is open sky: it has hedges 3 m tall with ladders
 * over them, and clamping there to a room's height left whatever the player was holding
 * hanging inside a hedge the moment they climbed past it. `null` rather than some large
 * number on purpose (design D1) — a guess at how tall a maze can get is a bug waiting for
 * the first taller maze.
 *
 * A missing level keeps the indoor default: nothing is being carried in that state, and
 * the conservative answer is the one that cannot let a box out through a roof (design D2).
 */
export function carryCeiling(level: LevelSpec | null): number | null {
  if (!level) return DEFAULT_CEILING_HEIGHT;
  if (level.grounds) return null;
  return level.ceilingHeight ?? DEFAULT_CEILING_HEIGHT;
}

/** Launch speed along the view direction, metres per second. */
export const THROW_SPEED = 8;
/** A little loft so a throw arcs instead of darting at the horizon. */
export const THROW_UPWARD_BIAS = 1.5;
/** How far in front of the eye a carried object floats, unobstructed. */
export const HOLD_DISTANCE = 1.2;
/**
 * The closest the hold point may come to the eye.
 *
 * Lower than arm's length, because when the way ahead is tight something has to give and
 * a box pushed through a wall is the worst of the options — that is a hole in the world,
 * and one an objective can be thrown through. It is not the whole answer: the player
 * controller also refuses to walk the box into anything, so this limit is reached by
 * turning on the spot in a corner rather than by walking, and only briefly.
 */
export const MIN_HOLD_DISTANCE = 0.35;

const MAX_SPIN = 5;

/**
 * Deterministic pseudo-random in [-1, 1] — the classic sine hash. Randomness in game
 * rules must be seeded (architectural rule 4); the throw index is the seed.
 */
function signedHash(seed: number): number {
  const noise = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return (noise - Math.floor(noise)) * 2 - 1;
}

/**
 * The tumble given to the Nth throw of the session. Same index, same tumble — a replay
 * with the same inputs behaves identically.
 */
export function throwAngularVelocity(throwIndex: number): Vec3 {
  return {
    x: signedHash(throwIndex * 3 + 1) * MAX_SPIN,
    y: signedHash(throwIndex * 3 + 2) * MAX_SPIN,
    z: signedHash(throwIndex * 3 + 3) * MAX_SPIN,
  };
}
